import { Link, useNavigate } from "react-router-dom";
import { FormActions } from "../../components/common/FormActions";
import { TTableColumn } from "../../types/common";
import { DEFAULT_EXCLUDED, DISBURSED } from "../../utils/constants";
import {
  beautify,
  createCustomAction,
  createEditAction,
  createViewAction,
  formatDate,
  NoWrap,
  returnObject,
  showObjectItems,
} from "../../utils/helpers";
import CachedIcon from "@mui/icons-material/Cached";
import { OpenOutline } from "@raresail/react-ionicons";

export const productTableColumns = [
  { id: "created_at", header: "Created At" },
  { id: "supplier_name", header: "Supplier" },
  { id: "vehicle_type_name", header: "Name" },
  {
    id: "registration_number",
    header: "Vehicle Identification Number",
    filter: {
      type: "text",
    },
  },
  {
    id: "status",
    header: "Status",
    filter: {
      type: "select",
      options: [
        { id: "in_stock", name: "In Stock" },
        { id: "disbursed", name: "Disbursed" },
        { id: "impounded", name: "Impounded" },
        { id: "stolen ", name: "Stolen" },
      ],
    },
  },
  {
    id: "number_plate",
    header: "Number Plate",
    filter: {
      type: "text",
    },
  },
];

export const useProduct = () => {
  const navigate = useNavigate();
  const tableColumns = (): TTableColumn[] => [
    ...productTableColumns,
    { id: "actions", header: "Actions" },
  ];

  const tableData = (data: any) =>
    data.map((item: any) => ({
      ...item,
      quantity: item?.quantity || "0",
      status: <NoWrap>{beautify(item?.status)}</NoWrap>,
      created_at: <NoWrap>{formatDate(item?.created_at)}</NoWrap>,
      vehicle_type_name: <NoWrap>{item?.vehicle_type_name}</NoWrap>,
      number_plate: <NoWrap>{item?.number_plate}</NoWrap>,
      actions: (
        <FormActions
          actions={[
            createViewAction("/products", item?.id),
            ...returnObject(![DISBURSED].includes(item?.status), [
              createEditAction("/products", item?.id),
            ]),
            createCustomAction(
              `/products_change_status/create/${item?.id}`,
              "Change Status",
              CachedIcon,
              () => {
                navigate(`/products_change_status/create/${item?.id}`);
              },
            ),
          ]}
        />
      ),
    }));

  const totalsData = (data: any) => {
    return {
      total: data?.count,
      total_disbursed: data?.metadata?.disbursed || 0,
      total_in_stock: data?.metadata?.in_stock || 0,
      total_impounded: data?.metadata?.impounded || 0,
      total_stolen: data?.metadata?.stolen || 0,
    };
  };

  const viewData = (item: any) => {
    return {
      sections: [
        {
          title: "Product Information",
          fields: {
            loan_application_number: (
              <Link
                to={`/loan_application/view/${item?.loan_application}`}
                className="link-with-icon"
              >
                {item?.loan_application_number && item?.loan_application_number}
                {item?.loan_application_number && <OpenOutline />}
              </Link>
            ),
            purchase_order_number: (
              <Link
                to={`/purchase_order/view/${item?.purchase_order}`}
                className="link-with-icon"
              >
                {item?.purchase_order_number}
                <OpenOutline />
              </Link>
            ),
            inventory_name: (
              <Link
                to={`/inventory/view/${item?.inventory}`}
                className="link-with-icon"
              >
                {item?.inventory_name}

                <OpenOutline />
              </Link>
            ),
            Vehicle_identification_number: item?.registration_number,
            ...showObjectItems(item, [
              ...DEFAULT_EXCLUDED,
              "registration_number",
              "loan_application",
              "loan_application_number",
              "purchase_order",
              "purchase_order_number",
              "purchase_order_inventory",
              "inventory",
              "inventory_name",
            ]),
          },
        },
      ],
    };
  };

  return { tableColumns, tableData, viewData, totalsData };
};
